import { Component, DestroyRef, LOCALE_ID, OnInit, ViewChild, inject, signal } from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgForm,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { DateAdapter, ErrorStateMatcher, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { distinctUntilChanged } from "rxjs/operators";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from "@angular/material-moment-adapter";

// Services
import { StoreUserService } from '@app/core/state/store-user.service';
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import { CreateUserService } from "../../../data/user/create-user.service";
import {
  DatabaseCacheService,
  Section,
} from "../../../data/cache/database-cache.service";

import dayjs from "dayjs";
import { from } from "rxjs";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { USER_CREATE_I18N } from "./user-create.i18n";

// 日付フォーマット定義（YYYY/MM/DD形式）
const DATE_FORMATS = {
  parse: {
    dateInput: 'YYYY/MM/DD',
  },
  display: {
    dateInput: 'YYYY/MM/DD',
    monthYearLabel: 'YYYY/MM',
    dateA11yLabel: 'YYYY/MM/DD',
    monthYearA11yLabel: 'YYYY/MM',
  },
};

@Component({
  selector: "app-user-create",
  templateUrl: "./user-create.component.html",
  styleUrls: ["./user-create.component.scss"],
  imports: SHARED_IMPORTS,
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (localeId: string) =>
        (localeId || '').toLowerCase().startsWith('en') ? 'en-US' : 'ja-JP',
      deps: [LOCALE_ID],
    },
  ],
})
export class CreateUserComponent implements OnInit {
  @ViewChild("submitButton") submitButton: HTMLButtonElement;

  private readonly destroyRef = inject(DestroyRef);

  isSending = signal(false);

  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = USER_CREATE_I18N;

  // Promise
  promise = Promise.resolve();

  // section info
  selectedSection: Section;

  lastNameENFormControl = new FormControl("", [
    Validators.required,
    Validators.pattern("[a-zA-Z0-9 ]*"),
  ]);

  firstNameENFormControl = new FormControl("", [
    Validators.required,
    Validators.pattern("[a-zA-Z0-9 ]*"),
  ]);

  createUserFormGroup: FormGroup;
  sexOptions = [
    { display: this.i18n.sex.male, value: "male" },
    { display: this.i18n.sex.female, value: "female" },
    { display: this.i18n.sex.other, value: "other" },
  ];
  selectedOption: string;
  matcher = new MyErrorStateMatcher();
  codeMode = true;
  postParam = [];
  postParamData;

  // Radio Cards pattern - registration method selection (Codex approved)
  registrationMethodControl = new FormControl<'code' | 'name'>('code');

  accountStatus = [
    { value: "OPEN", viewValue: this.i18n.status.active },
    { value: "CLOSE", viewValue: this.i18n.status.inactive },
  ];
  selectedStatus = "";

  accountGroup = [
    { value: "STAFF", viewValue: this.i18n.accountGroup.staff },
    { value: "USER", viewValue: this.i18n.accountGroup.user },
  ];
  selectedGroup = "";

  accountManageRole = false;
  userManageRole = false;
  measureRole = false;
  referRole = false;

  userId: string;
  sectionId: string;

  /** バックリンクのルート */
  get backRoute(): string[] {
    return ['/section-admin', this.selectedSection?.sectionId, 'user-list'];
  }

  /** バックリンクのラベル */
  get backLabel(): string {
    return this.i18n.backLink.userList;
  }

  /** バックリンクのアイコン */
  readonly backIcon = 'face';

  constructor(
    public storeUser: StoreUserService,
    public storeAuth: StoreAuthService,
    public snackBar: MatSnackBar,
    private createUserService: CreateUserService,
    private common: DatabaseCacheService,
  ) {}

  ngOnInit(): void {
    this.createUserFormGroup = new FormGroup({
      code: new FormControl("", [Validators.required]),
      lastName: new FormControl<string | null>(null),
      firstName: new FormControl<string | null>(null),
      contractStartAt: new FormControl<Date | null>(new Date(), [Validators.required]),
      birthDate: new FormControl<Date | null>(null),
      sex: new FormControl("", [Validators.pattern("^(male|female|other)$")]),
    });

    this.createUserFormGroup
      .get("sex")
      .valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((selectedOption) => {
        this.createUserFormGroup.get("sex").setValue(selectedOption);
      });

    // section
    this.selectedSection = this.common.getSelectedSection();
    this.common.parametersUpdated.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((noData) => {
      // Sectionに関わるデータの読み込み
      this.selectedSection = this.common.getSelectedSection();
    });

    // Radio Cards registration method selection (Codex approved)
    this.registrationMethodControl.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      if (value === 'code') {
        this.codeClickEvent();
      } else {
        this.nameClickEvent();
      }
    });
  }

  get codeFormControl() {
    return this.createUserFormGroup.get("code");
  }

  get lastNameFormControl() {
    return this.createUserFormGroup.get("lastName");
  }

  get firstNameFormControl() {
    return this.createUserFormGroup.get("firstName");
  }

  get contractStartAtFormControl() {
    return this.createUserFormGroup.get("contractStartAt");
  }

  get birthDateFormControl() {
    return this.createUserFormGroup.get("birthDate");
  }

  get sexFormControl() {
    return this.createUserFormGroup.get("sex");
  }

  codeClickEvent() {
    if (!this.codeMode || this.createUserFormGroup.get("code").pristine) {
      this.codeMode = true;

      this.lastNameFormControl.setValidators([]);
      this.lastNameFormControl.reset();

      this.firstNameFormControl.setValidators([]);
      this.firstNameFormControl.reset();

      this.codeFormControl.setValidators([Validators.required]);
      this.codeFormControl.reset();
    }
  }

  nameClickEvent() {
    if (this.codeMode) {
      this.codeMode = false;

      this.codeFormControl.setValidators([]);
      this.codeFormControl.reset();

      this.lastNameFormControl.setValidators([Validators.required]);
      this.firstNameFormControl.setValidators([Validators.required]);

      this.lastNameFormControl.reset();
      this.firstNameFormControl.reset();
    }
  }

  update() {
    this.createUser();
  }

  submit() {
  }

  resetFields(): void {
    // this.createUserForm.resetForm();
    this.createUserFormGroup.reset();
    this.selectedOption = "";
  }

  createUser() {
    const sectionId = this.selectedSection?.sectionId;
    if (!sectionId) return;

    const formData = this.createUserFormGroup.value;

    let birthDate: string | null = null;
    const birthDateValue = this.birthDateFormControl.value;
    if (birthDateValue) {
      birthDate = dayjs(birthDateValue).toISOString();
    }

    const contractStartAtValue = this.contractStartAtFormControl.value;
    const contractStartAt = contractStartAtValue
      ? dayjs(contractStartAtValue).toISOString()
      : null;

    this.postParamData = {
      accountEnabled: "true",
      code: this.codeFormControl.value || null,
      firstName: this.firstNameFormControl.value || null,
      lastName: this.lastNameFormControl.value || null,
      jobTitleCode: "NU",
      birthDate: birthDate,
      sex: this.sexFormControl.value || null,
      contractStartAt: contractStartAt,
    };
    this.postParam = [this.postParamData];

    this.isSending.set(true);
    this.createUserService.createUser(this.postParam, sectionId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res) => {
        this.isSending.set(false);
        this.common.parametersUpdated.next({
          type: 'usersListUpdated',
          sectionId: this.selectedSection?.sectionId ?? null,
        });
        let snack = this.snackBar.open(
          this.i18n.message.createSuccess,
          this.i18n.button.close,
          { duration: 3000 },
        );
        snack.afterDismissed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.resetFields();
        });
      },
      (error) => {
        this.isSending.set(false);
        this.snackBar.open(
          this.i18n.message.createError,
          this.i18n.button.close,
          { duration: 10000 },
        );
      },
    );
  }
}

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null,
  ): boolean {
    const isSubmitted = form && form.submitted;
    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || isSubmitted)
    );
  }
}
